/* eslint-disable max-lines */
import { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import styled from 'styled-components';
import QueryString from 'query-string';
import { Link } from 'react-router-dom';
import { Tooltip } from '@material-ui/core';
import { TeamMembersBar } from 'views/root/views/home/views/dashboard/components/TeamMembersBar';
import Text, { Inline } from 'components/base/Text';
import Table from 'components/base/Table';
import { getColor } from 'common/constants/_colors';
import { matchContributorMetricsToContributor } from 'common/services/contributors.service';
import AppRouter from 'common/AppRouter';
import { getContributorAvatarUrl } from 'common/constants/Avatars';
import { nullSafeGet } from 'common/utils/utils';
import { Flex } from '../base/layout-components';
import MultipleAvatars from '../MultipleAvatars';
import RelativeCircle from '../RelativeCircle';
import { metricNames } from '@linearb/linjs-common';
import BranchStateActiveTableValue from './BranchStateActiveTableValue';
import TeamContributorsSkeleton from 'images/teamContributorsSkeleton.svg';
import {
  DEFAULT_METRIC_VALUE,
  getDataMetricToColumnConfigMap,
  getHeaderComponent,
  getNameColumnHeaderComponent,
  PlaceHolderCircle,
  PlaceHolderRow
} from './util';
import { SIZES } from 'common/constants/sizes';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import * as TrackingService from 'common/services/tracking.service';
import Burnout from '../ContributorsRosterByTeamHidePersonalInfo/Burnout';
import {
  EMPTY_NUMBER,
  LOCAL_STORAGE_KEYS,
  TRACKING,
  SEARCH_DATE_FORMAT,
  DAYS_PARAM
} from 'common/constants/constants';
import {
  ContributorManagementMenuItems,
  MergedUserIcon
} from 'components/ContributorManagementMenu/ContributorManagementMenu';
import useTeams from 'views/root/views/home/views/settings/views/ContributorsAndTeams/Teams/useTeams';
import { useContributorsOrUsers } from 'components/ContributorsRosterByTeamHidePersonalInfo/useContributorsOrUsers';

export const LinkWithoutUnderline = styled(Link)`
  text-decoration: none;
  color: ${getColor('black')};
`;
const BlueTag = styled.div`
  background-color: ${getColor('dodgerBlue')};
  color: ${getColor('white')};
  padding: 0.2rem 0.4rem;
  border-radius: 0.2rem;
  margin-left: 0.5rem;
  cursor: default;
  font-weight: bold;
  width: fit-content;
`;
const YouTag = () => <BlueTag>YOU</BlueTag>;

const getData = (
  contributors,
  metrics,
  currentIteration,
  isRetrospect,
  teams,
  myAccount,
  isFinishedOnBoarding
) => {
  const contributorsIds = _.keyBy(contributors, 'id');

  const myProviderId = nullSafeGet(
    myAccount,
    'settings.provider_id',
    EMPTY_NUMBER
  ).toString();
  if (!isFinishedOnBoarding && !metrics.length) {
    // on onboarding - before we have team metrics:
    const teamContributorsIds = _.get(teams, '[0].contributor_ids', []);
    return contributors.length
      ? contributors
          .filter(({ id }) => teamContributorsIds.includes(id))
          .map(({ email, id, name, username, extended_data }) => ({
            name: (
              <Flex items='center'>
                {/* @ts-ignore */}
                <MultipleAvatars
                  disableTooltip={!email}
                  avatars={[
                    {
                      name: email,
                      url: getContributorAvatarUrl({ id, extended_data })
                    }
                  ]}
                />
                <Flex pl='small' direction='column' items='left'>
                  <Text
                    pl={SIZES.small}
                    cursor='pointer'
                    size='large'
                    weight={400}
                    ellipsisAfter='13rem'
                  >
                    {name ? name : username}
                  </Text>
                </Flex>
                {extended_data.provider_id === myProviderId && <YouTag />}
              </Flex>
            ),
            [metricNames.ACTIVE_BRANCHES]: <PlaceHolderRow />,
            [metricNames.DONE_BRANCHES]: <PlaceHolderCircle />,
            [metricNames.REVIEWS]: <PlaceHolderCircle />
          }))
      : null;
  }
  let metricsWithEmpty;
  if (!isFinishedOnBoarding) {
    const teamContributorsIds = [..._.get(teams, '[0].contributor_ids', [])];
    metrics.forEach(({ contributor_id }) =>
      _.remove(teamContributorsIds, id => id === contributor_id)
    );
    const emptyContributors = teamContributorsIds.map(id => ({
      contributor_id: id,
      hasNoData: true
    }));
    metricsWithEmpty = [...metrics, ...emptyContributors];
  }
  const search = QueryString.parse(window.location.search);
  const maxWip = metrics.reduce(
    (acc, cur) => Math.max(acc, cur[metricNames.ACTIVE_BRANCHES]),
    0
  );
  const iterationDays =
    moment(moment(isRetrospect ? currentIteration.end_date : undefined)).diff(
      moment(currentIteration.start_date),
      DAYS_PARAM
    ) + 1;
  return matchContributorMetricsToContributor(
    contributors,
    isFinishedOnBoarding ? metrics : metricsWithEmpty
  ).map(contributorMetric => {
    const {
      contributor_id: contributorId,
      name,
      username,
      avatarUrl,
      providerId,
      hasNoData,
      team = {}
    } = contributorMetric;
    let hasEmptyData = false;
    const contribToLink = {
      pathname: AppRouter.BRANCH.path,
      search: QueryString.stringify({
        metric_name: metricNames.ACTIVE_BRANCHES,
        contributors: contributorId,
        time_range: [
          moment(currentIteration.start_date).format(SEARCH_DATE_FORMAT),
          moment
            .min(moment(currentIteration.end_date), moment())
            .format(SEARCH_DATE_FORMAT)
        ].join(',')
      })
    };
    const { is_parent: isParent = false, contained_ids: childIds = [] } =
      contributorsIds[contributorId];

    const reviews =
      _.get(contributorMetric, metricNames.REVIEWS, DEFAULT_METRIC_VALUE) ||
      DEFAULT_METRIC_VALUE;
    const doneCount =
      _.get(
        contributorMetric,
        metricNames.DONE_BRANCHES,
        DEFAULT_METRIC_VALUE
      ) || DEFAULT_METRIC_VALUE;
    const activeCount =
      _.get(
        contributorMetric,
        metricNames.ACTIVE_BRANCHES,
        DEFAULT_METRIC_VALUE
      ) || DEFAULT_METRIC_VALUE;
    const activityDays =
      _.get(contributorMetric, metricNames.ACTIVE_DAYS, DEFAULT_METRIC_VALUE) ||
      DEFAULT_METRIC_VALUE;
    const reviewDepth =
      _.get(
        contributorMetric,
        metricNames.REVIEW_DEPTH,
        DEFAULT_METRIC_VALUE
      ) || DEFAULT_METRIC_VALUE;

    const isBurnout = activityDays > 6 && activityDays / iterationDays >= 0.9;
    const styles = team.hasLeftOnly ? { grayedOut: true } : {};
    const firstName = name ? name.split(' ')[0] : username;
    const activityTooltipText = `${
      activeCount ? `${activeCount} ` : ''
    }active branch${activeCount === 1 ? '' : 'es'}`;

    const tooltipTitle = activeCount
      ? `View ${firstName}’s ${activityTooltipText}`
      : `${firstName} has no ${activityTooltipText}`;
    if (!isFinishedOnBoarding) {
      hasEmptyData =
        hasNoData ||
        reviews + doneCount + activeCount + activityDays + reviewDepth;
    }
    const nameCol = (
      <Flex items={team.info ? 'baseline' : 'center'} direction='row'>
        <Link
          style={{ textDecoration: 'none', color: getColor('black') }}
          to={contribToLink}
        >
          <Tooltip title={tooltipTitle}>
            <Flex items='center' direction='row'>
              {/* @ts-ignore */}
              <MultipleAvatars
                changeCursor
                avatars={[{ url: avatarUrl }]}
                disableTooltip
              />
              <Flex pl='small' direction='column' items='left'>
                <Flex>
                  <Inline size='large' weight={400}>
                    <Text
                      cursor='pointer'
                      size='large'
                      weight={400}
                      ellipsisAfter='13rem'
                    >
                      {name ? name : username}
                    </Text>
                  </Inline>
                  {providerId === myProviderId && <YouTag />}
                </Flex>
                {team.info && (
                  <Inline size='small' weight={400} color='gray'>
                    {team.info}
                  </Inline>
                )}
              </Flex>
            </Flex>
          </Tooltip>
        </Link>
        {isParent && <MergedUserIcon childIds={childIds} />}
      </Flex>
    );

    return {
      styles,
      menu: (
        <ContributorManagementMenuItems
          currentContributor={contributorsIds[contributorId]}
        />
      ),
      name: nameCol,
      [metricNames.ACTIVE_BRANCHES]: hasEmptyData ? (
        <PlaceHolderRow />
      ) : (
        <BranchStateActiveTableValue
          activeCount={activeCount}
          maxActiveCount={maxWip}
          isRetrospect={isRetrospect}
          name={firstName}
          link={{
            pathname: AppRouter.BRANCH.path,
            search: QueryString.stringify({
              metric_name: metricNames.ACTIVE_BRANCHES,
              contributors: contributorId,
              time_range: [
                moment(currentIteration.start_date).format(SEARCH_DATE_FORMAT),
                moment
                  .min(moment(currentIteration.end_date), moment())
                  .format(SEARCH_DATE_FORMAT)
              ].join(',')
            })
          }}
        />
      ),
      [metricNames.DONE_BRANCHES]: hasEmptyData ? (
        <PlaceHolderCircle />
      ) : (
        <Flex items='center'>
          <LinkWithoutUnderline
            to={{
              pathname: AppRouter.BRANCH.path,
              search: QueryString.stringify({
                metric_name: metricNames.DONE_BRANCHES,
                contributors: contributorId,
                time_range: [
                  moment(currentIteration.start_date).format(
                    SEARCH_DATE_FORMAT
                  ),
                  moment
                    .min(moment(currentIteration.end_date), moment())
                    .format(SEARCH_DATE_FORMAT)
                ].join(','),
                ...(search.teams ? { teams: search.teams } : {})
              })
            }}
          >
            <Inline size='large' pr='0.5rem' weight={400}>
              {doneCount}
            </Inline>
          </LinkWithoutUnderline>
        </Flex>
      ),
      [metricNames.REVIEWS]: hasEmptyData ? (
        <PlaceHolderCircle />
      ) : (
        <Flex items='center'>
          <Tooltip title={`Avg review depth: ${reviewDepth}`}>
            <span>
              <RelativeCircle
                displayValue={reviews}
                value={reviewDepth}
                valuesConfig={[0.5, 1.5, 2.5, 4]}
              />
            </span>
          </Tooltip>
        </Flex>
      ),
      [metricNames.ACTIVE_DAYS]: hasEmptyData ? null : (
        <Flex items='center'>
          <Inline
            size='large'
            pr='1rem'
            weight={400}
          >{`${activityDays}/${iterationDays}`}</Inline>
          {/* @ts-ignore */}
          {isBurnout && <Burnout name={firstName} showLabel={false} />}
        </Flex>
      )
    };
  });
};

const ContributorsRosterByTeam = props => {
  const {
    currentIteration,
    isRetrospect = false,
    contributorsMetrics,
    teamMetrics,
    order,
    onOrderChange,
    selectedTeam,
    teams,
    myAccount,
    isFinishedOnBoarding
  } = props;
  const contributors = useContributorsOrUsers();
  const { onConfigureTeam } = useTeams();
  const localStorageAccessor = BrowserStorageAccessor().local;

  const [showIsMyTeam, setShowIsMyTeam] = useState(
    !!localStorageAccessor.get(LOCAL_STORAGE_KEYS.SHOW_IS_THAT_YOUR_TEAM_BAR) &&
      contributors.length
  );

  useEffect(() => {
    if (
      localStorageAccessor.get(LOCAL_STORAGE_KEYS.SHOW_IS_THAT_YOUR_TEAM_BAR)
    ) {
      setShowIsMyTeam(!!contributors.length);
    }
  }, [contributors]);

  const getColumns = ({
    teamId,
    teamMetrics = {},
    currentIteration,
    isRetrospect,
    hideValues
  }) => [
    {
      dataPoint: 'name',
      ColumnHeaderComponent: () =>
        getNameColumnHeaderComponent({
          selectedTeam,
          currentIteration,
          onConfigureTeam
        })
    },
    ...Object.entries(
      getDataMetricToColumnConfigMap(teamId, currentIteration, isRetrospect)
    ).map(([metric, { name, link, logEvent, tooltipTitle }]) => ({
      dataPoint: metric,
      isSortedCol: true,
      ColumnHeaderComponent: getHeaderComponent(
        teamMetrics[metric],
        link,
        hideValues,
        logEvent,
        tooltipTitle
      ),
      name
    })),
    {
      dataPoint: 'menu',
      name: '',
      showOnlyOnHover: true,
      tableCellProps: { style: { borderLeft: 0, maxWidth: '2.5rem' } }
    }
  ];
  const nonEmptyContributorsMetrics =
    contributorsMetrics.length && contributorsMetrics;
  const data = useMemo(
    () =>
      getData(
        contributors,
        contributorsMetrics,
        currentIteration,
        isRetrospect,
        teams,
        myAccount,
        isFinishedOnBoarding
      ),
    [
      contributors,
      nonEmptyContributorsMetrics,
      currentIteration,
      isRetrospect,
      teams,
      myAccount
    ]
  );
  const columns = getColumns({
    teamId: selectedTeam.id,
    teamMetrics,
    currentIteration,
    isRetrospect,
    hideValues: !isFinishedOnBoarding
  });
  return data ? (
    <Flex direction='column' width='100%' height='100%'>
      {showIsMyTeam && (
        <TeamMembersBar
          onEditTeamClick={() => {
            TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
              page: TRACKING.PAGES.DASHBOARD,
              click_type: TRACKING.CLICK.EDIT_TEAM
            });
            // @ts-ignore
            onConfigureTeam({ team: selectedTeam });
          }}
          onThisIsMyTeamClick={() => {
            TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
              page: TRACKING.PAGES.DASHBOARD,
              click_type: TRACKING.CLICK.CONFIRM_TEAM
            });
            BrowserStorageAccessor().local.remove(
              LOCAL_STORAGE_KEYS.SHOW_IS_THAT_YOUR_TEAM_BAR
            );
            setShowIsMyTeam(false);
          }}
        />
      )}
      <Flex height={showIsMyTeam ? 'calc(100% - 5rem)' : '100%'} grow={1}>
        {/* @ts-ignore */}
        <Table
          tableComponentProps={{ stickyHeader: true }}
          sortBy={order.by}
          sortOrder={order.dir}
          onSort={(colName, order) =>
            onOrderChange({ by: colName, dir: order })
          }
          columns={columns}
          data={data}
          lineHighlightOnHover
        />
      </Flex>
    </Flex>
  ) : (
    <img src={TeamContributorsSkeleton} alt='' />
  );
};

export default ContributorsRosterByTeam;
